/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { useState, type FC, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { useGlobalStore } from '../../../stores/global/global.store';
import { useUserProfileStore } from '../../../stores/user-profile/user-profile.store';
import { Divider } from '../../../ui-library/Elements/Divider';
import { AccountProviders } from '../../../utils/providers';
import { LogoutButton } from '../../LogoutButton';
import { ProviderSelector as GenericProviderSelector } from '../../ProviderSelector';

import AddCircleIcon from '@icons/AddCircle.svg';
import EpicIcon from '@icons/Platforms/Epic.svg';
import MicrosoftIcon from '@icons/Platforms/Microsoft.svg';
import PlaystationIcon from '@icons/Platforms/Playstation.svg';
import SteamIcon from '@icons/Platforms/Steam.svg';
import SwitchIcon from '@icons/Platforms/Switch.svg';
import XboxIcon from '@icons/Platforms/Xbox.svg';
import RefreshIcon from '@icons/Refresh.svg';
import RefreshCircleIcon from '@icons/RefreshCircle.svg';
import UserIcon from '@icons/User.svg';

interface Props {
  providers: AccountProviders['accountProviders'];
  accountStatus: AccountProviders['accountStatus'];
  providersEnabled: boolean | null;
}

const providerIcons: Record<string, FC<SVGProps<SVGElement>>> = {
  psn: PlaystationIcon,
  steam: SteamIcon,
  xbox: XboxIcon,
  grdk: MicrosoftIcon,
  egs: EpicIcon,
  nintendo: SwitchIcon,
};

export const ProviderSelector = ({ providers, accountStatus, providersEnabled }: Props) => {
  const { t: tCommon } = useTranslation('common', { keyPrefix: 'common' });
  const [isOpen, setIsOpen] = useState(false);
  const { selectedProvider, setSelectedProvider } = useGlobalStore();
  const { nickName } = useUserProfileStore();

  const emptyContent = {
    type: 'none',
    title: tCommon('add-platform'),
    username: nickName,
    icon: UserIcon,
  };

  const syncedContent = {
    type: 'synced',
    title: tCommon('cross-prog-enabled'),
    username: nickName,
    icon: RefreshCircleIcon,
  };

  const providerSelectorContent = providers.map((provider) => {
    const providerTitle = tCommon(`providers.${provider.provider}`);
    return {
      type: provider.provider,
      title: providerTitle,
      username: provider.userName || providerTitle,
      icon: providerIcons[provider.provider] || UserIcon,
    };
  });

  const changeCallback = (value: string) => {
    setSelectedProvider(value);
    setIsOpen(false);
  };

  const buttonClasses = classNames(
    'flex items-center justify-start gap-2',
    'cursor-pointer',
    'transition-colors duration-300',
    'hover:text-neutral-text-xlight',
    'focus-visible:bg-surface-dark focus-visible:outline-offset-2',
  );

  return (
    <GenericProviderSelector
      className='items-end'
      buttonClassName='w-full'
      onChange={(value) => {
        changeCallback(value);
      }}
      active={isOpen}
      onToggle={(value) => {
        setIsOpen(value);
      }}
      synced={accountStatus === 'synced'}
      providers={providerSelectorContent}
      selectedProvider={selectedProvider}
      syncedProvider={syncedContent}
      emptyProvider={emptyContent}
    >
      {accountStatus === 'synced' ? (
        <>
          <p className='text-xs font-normal text-neutral-text-medium'>{tCommon('cross-prog-enabled')}</p>
          <button
            key='providers-sync'
            onClick={() => {
              setIsOpen(false);
              void open(`${process.env.NEXT_PUBLIC_FE_BA_URL}/account/my-account/games/dbd`);
            }}
            className={buttonClasses}
            data-cy='sync-provider-btn'
          >
            <RefreshIcon className='pointer-events-none relative size-5 shrink-0 text-neutral-text-medium' />
            {tCommon('sync-more')}
          </button>
        </>
      ) : (
        <>
          <button
            key='providers-add'
            onClick={() => {
              setIsOpen(false);
              void open(`${process.env.NEXT_PUBLIC_FE_BA_URL}/account/my-account/providers`);
            }}
            className={buttonClasses}
            data-cy='add-provider-btn'
          >
            <AddCircleIcon className='pointer-events-none relative size-5 shrink-0 text-neutral-text-medium' />
            {tCommon('add-platform')}
          </button>
          {providersEnabled && (
            <button
              key='providers-sync'
              onClick={() => {
                setIsOpen(false);
                void open(`${process.env.NEXT_PUBLIC_FE_BA_URL}/account/my-account/games/dbd`);
              }}
              className={buttonClasses}
              data-cy='sync-provider-btn'
            >
              <RefreshCircleIcon className='pointer-events-none relative size-5 shrink-0 text-neutral-text-medium' />
              {tCommon('sync-progress')}
            </button>
          )}
        </>
      )}
      <Divider />
      <LogoutButton className={buttonClasses} />
    </GenericProviderSelector>
  );
};
